const CACHE_TTL = 1000 * 60 * 15; // 15 minutes

const cacheKey = (url) => 'rss_v3_' + btoa(unescape(encodeURIComponent(url))).slice(0, 24);

const getCached = (url) => {
  try {
    const raw = localStorage.getItem(cacheKey(url));
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return data?.episodes?.length > 0 ? data : null;
  } catch { return null; }
};

const setCache = (url, data) => {
  try {
    localStorage.setItem(cacheKey(url), JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* ignore storage full */ }
};

const parseItems = (xmlText) => {
  const cleaned = xmlText.replace(/^\uFEFF/, '').trim();
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, 'text/xml');

  // Extract Channel Info dynamically
  const channel = doc.querySelector('channel');
  let chanTitle = '', chanDesc = '', chanImg = '';
  
  if (channel) {
    chanTitle = channel.querySelector('title')?.textContent?.trim() || '';
    chanDesc = channel.querySelector('description')?.textContent?.trim() || '';
    chanImg = channel.querySelector('image > url')?.textContent?.trim() || 
              channel.getElementsByTagName('itunes:image')[0]?.getAttribute('href') || '';
  } else {
    // Atom fallback
    chanTitle = doc.querySelector('feed > title')?.textContent?.trim() || '';
    chanDesc = doc.querySelector('feed > subtitle')?.textContent?.trim() || '';
    chanImg = doc.querySelector('feed > icon')?.textContent?.trim() || 
              doc.querySelector('feed > logo')?.textContent?.trim() || '';
  }

  let items = Array.from(doc.getElementsByTagName('item'));
  if (items.length === 0) items = Array.from(doc.getElementsByTagName('entry'));

  const getText = (el, tag) => {
    const found = el.getElementsByTagName(tag)[0];
    return found ? (found.textContent || '').trim() : '';
  };

  const episodes = items.slice(0, 15).map((item, i) => {
    let audioUrl = '';
    
    // 1. Enclosure
    const enclosure = item.querySelector('enclosure');
    if (enclosure) audioUrl = enclosure.getAttribute('url') || '';

    // 2. Media Content
    if (!audioUrl) {
      const mediaContent = item.getElementsByTagName('media:content')[0];
      if (mediaContent && (mediaContent.getAttribute('type') || '').includes('audio')) {
        audioUrl = mediaContent.getAttribute('url') || '';
      }
    }
    
    // 3. Atom Link
    if (!audioUrl) {
      for (const l of item.querySelectorAll('link')) {
        if (l.getAttribute('rel') === 'enclosure') { audioUrl = l.getAttribute('href') || ''; break; }
      }
    }

    const itunesImg = item.getElementsByTagName('itunes:image')[0]?.getAttribute('href');
    const image = itunesImg || chanImg || '';
    const title = getText(item, 'title') || `Episode ${i + 1}`;
    const rawDate = getText(item, 'pubDate') || getText(item, 'published') || '';
    const pubDate = rawDate ? new Date(rawDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const duration = getText(item, 'itunes:duration');
    const rawDesc = getText(item, 'description') || getText(item, 'summary') || '';
    const description = rawDesc.replace(/<[^>]*>/gm, '').trim().slice(0, 220);

    return {
      title,
      description: description ? description + '…' : '',
      pubDate,
      duration,
      image,
      audioUrl,
      guid: getText(item, 'guid') || getText(item, 'id') || audioUrl,
    };
  }).filter(ep => ep.audioUrl);

  return { channel: { title: chanTitle, description: chanDesc, image: chanImg }, episodes };
};

const fetchWithTimeout = async (url, timeout = 12000) => {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeout);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    clearTimeout(id);
    return res;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
};

// Strategic fallbacks: JSON-wrapped endpoints bypass browser CORS blocks better than raw text endpoints
const strategies = [
  async (url) => {
    console.log('[RSS] Strategy 1: allorigins JSON');
    const res = await fetchWithTimeout(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`allorigins HTTP ${res.status}`);
    const data = await res.json();
    if (!data.contents) throw new Error('allorigins empty contents');
    return parseItems(data.contents);
  },
  async (url) => {
    console.log('[RSS] Strategy 2: rss2json API');
    const res = await fetchWithTimeout(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`rss2json HTTP ${res.status}`);
    const data = await res.json();
    if (data.status !== 'ok') throw new Error('rss2json status not ok');
    
    const chanImg = data.feed?.image || '';
    const episodes = data.items.map((item, i) => {
      let audioUrl = item.enclosure?.link;
      if (!audioUrl && typeof item.enclosure === 'string') audioUrl = item.enclosure;
      if (!audioUrl) return null;
      
      const desc = (item.description || item.content || '').replace(/<[^>]*>/g, '').trim().slice(0, 220);
      return {
        title: item.title || `Episode ${i + 1}`,
        description: desc ? desc + '…' : '',
        pubDate: item.pubDate ? new Date(item.pubDate.replace(/-/g, '/')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
        duration: '', // rss2json free tier often drops duration
        image: item.thumbnail || chanImg,
        audioUrl,
        guid: item.guid || audioUrl
      };
    }).filter(Boolean);

    return {
      channel: { title: data.feed?.title || '', description: data.feed?.description || '', image: chanImg },
      episodes
    };
  },
  async (url) => {
    console.log('[RSS] Strategy 3: corsproxy.io');
    const res = await fetchWithTimeout(`https://corsproxy.io/?${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`corsproxy.io HTTP ${res.status}`);
    const text = await res.text();
    return parseItems(text);
  }
];

export const fetchPodcastFeed = async (rssUrl, onPartial) => {
  // 1. Return cache immediately if valid
  const cached = getCached(rssUrl);
  if (cached?.episodes?.length > 0) {
    onPartial?.(cached);
    // Background refresh
    (async () => {
      for (const strategy of strategies) {
        try {
          const data = await strategy(rssUrl);
          if (data?.episodes?.length > 0) { 
            setCache(rssUrl, data); 
            onPartial?.(data); 
            break; 
          }
        } catch { /* ignore background errors */ }
      }
    })();
    return cached;
  }

  // 2. Fetch fresh
  let lastErr = null;
  for (const strategy of strategies) {
    try {
      const data = await strategy(rssUrl);
      if (data?.episodes?.length > 0) {
        setCache(rssUrl, data);
        onPartial?.(data);
        return data;
      }
    } catch (err) {
      console.warn(`[RSS] Strategy failed:`, err.message);
      lastErr = err;
    }
  }

  throw new Error(lastErr?.message || 'Failed to fetch feed from all available proxy sources.');
};